import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { PersonProjectsActions } from '../actions/person-projects.actions';
import { PersonProjectDto } from '../dtos/person-project-dto.interface';

/** REV-22155 — person projects; apart from the company projects store. */
export interface PersonProjectsState extends EntityState<PersonProjectDto> {
    activeProjectId: string | null;
    totalCount: number;
    loading: boolean;
    error: string | null;
}

export const personProjectsAdapter = createEntityAdapter<PersonProjectDto>({
    selectId: (project) => project.projectId,
});

export const initialPersonProjectsState: PersonProjectsState = personProjectsAdapter.getInitialState({
    activeProjectId: null,
    totalCount: 0,
    loading: false,
    error: null,
});

const loading = (state: PersonProjectsState): PersonProjectsState => ({ ...state, loading: true });
const failed = (state: PersonProjectsState, { error }: { error: { message: string } }): PersonProjectsState => ({
    ...state,
    loading: false,
    error: error.message,
});
const done = (state: PersonProjectsState): PersonProjectsState => ({ ...state, loading: false, error: null });

export const personProjectsReducer = createReducer(
    initialPersonProjectsState,

    on(PersonProjectsActions.resetPersonProjects, () => initialPersonProjectsState),
    on(PersonProjectsActions.loadPersonProjects, loading),
    on(PersonProjectsActions.personProjectsLoaded, (state, { response }) =>
        personProjectsAdapter.setAll(response.personProjects, { ...done(state), totalCount: response.totalCount }),
    ),
    on(PersonProjectsActions.personProjectsLoadFailure, failed),

    on(PersonProjectsActions.loadPersonProject, loading),
    on(PersonProjectsActions.personProjectLoaded, (state, { project }) =>
        personProjectsAdapter.upsertOne(project, done(state)),
    ),
    on(PersonProjectsActions.personProjectLoadFailure, failed),

    on(PersonProjectsActions.createPersonProject, loading),
    on(PersonProjectsActions.personProjectCreated, (state, { project }) =>
        personProjectsAdapter.addOne(project, { ...done(state), totalCount: state.totalCount + 1 }),
    ),
    on(PersonProjectsActions.personProjectCreateFailure, failed),

    on(PersonProjectsActions.updatePersonProjectStatus, loading),
    on(PersonProjectsActions.personProjectStatusUpdated, (state, { project }) =>
        personProjectsAdapter.upsertOne(project, done(state)),
    ),
    on(PersonProjectsActions.personProjectStatusUpdateFailure, failed),

    on(PersonProjectsActions.bulkUpdatePersonProjectStatus, loading),
    on(PersonProjectsActions.bulkPersonProjectStatusUpdated, (state, { projects }) =>
        personProjectsAdapter.upsertMany(projects, done(state)),
    ),
    on(PersonProjectsActions.bulkUpdatePersonProjectStatusFailure, failed),

    on(PersonProjectsActions.deletePersonProject, loading),
    on(PersonProjectsActions.personProjectDeleted, (state, { projectId }) =>
        personProjectsAdapter.removeOne(projectId, { ...done(state), totalCount: Math.max(0, state.totalCount - 1) }),
    ),
    on(PersonProjectsActions.personProjectDeleteFailure, failed),

    on(PersonProjectsActions.bulkDeletePersonProjects, loading),
    on(PersonProjectsActions.bulkPersonProjectsDeleted, (state, { deletedIds }) =>
        personProjectsAdapter.removeMany(deletedIds, {
            ...done(state),
            totalCount: Math.max(0, state.totalCount - deletedIds.length),
        }),
    ),
    on(PersonProjectsActions.bulkDeletePersonProjectsFailure, failed),

    on(PersonProjectsActions.setActivePersonProjectId, (state, { projectId }) => ({
        ...state,
        activeProjectId: projectId,
    })),
    on(PersonProjectsActions.clearActivePersonProjectId, (state) => ({ ...state, activeProjectId: null })),
);

export const personProjectsFeature = createFeature({
    name: 'aumicoPersonProjectsFeature',
    reducer: personProjectsReducer,
});

const { selectAll, selectEntities } = personProjectsAdapter.getSelectors();

export const selectPersonProjectsState = personProjectsFeature.selectAumicoPersonProjectsFeatureState;
export const selectPersonProjects = createSelector(selectPersonProjectsState, selectAll);
export const selectPersonProjectsEntities = createSelector(selectPersonProjectsState, selectEntities);
export const selectPersonProjectsTotalCount = createSelector(selectPersonProjectsState, (state) => state.totalCount);
export const selectPersonProjectsLoading = createSelector(selectPersonProjectsState, (state) => state.loading);
export const selectActivePersonProjectId = createSelector(selectPersonProjectsState, (state) => state.activeProjectId);
export const selectActivePersonProject = createSelector(
    selectPersonProjectsEntities,
    selectActivePersonProjectId,
    (entities, projectId) => (projectId != null ? entities[projectId] : undefined),
);
